import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { crmSalesStatuses } from 'src/redux/slices/crm-sales';
import { convertToLocalISO, getDateRangePreset } from 'src/utils';
import {
  CreateCrmSalesTicketPayload,
  CrmSalesFilterPayload,
  CrmSalesStatus,
  UpdateCrmSalesTicketPayload
} from 'src/redux/slices/crm-sales/types';

const {
  actions: crmSalesActions,
  selectors: crmSalesSelectors
} = slices.crmSales;

const getDefaultTodayFilters = (): CrmSalesFilterPayload => {
  const range = getDateRangePreset('today');

  if (!range) return {};

  return {
    start_date: convertToLocalISO(range.start) || undefined,
    end_date: convertToLocalISO(range.end, true) || undefined
  };
};

const getFetchFilters = (
  filters?: CrmSalesFilterPayload
): CrmSalesFilterPayload => ({
  ...getDefaultTodayFilters(),
  ...filters
});

export const useCrmSalesTicket = () => {
  const dispatch = useAppDispatch();

  const columns = useAppSelector(crmSalesSelectors.selectCrmSalesColumns);

  const leadColumn = useAppSelector(
    crmSalesSelectors.selectCrmSalesColumnByStatus('Lead')
  );
  const warmColumn = useAppSelector(
    crmSalesSelectors.selectCrmSalesColumnByStatus('Warm')
  );
  const engageColumn = useAppSelector(
    crmSalesSelectors.selectCrmSalesColumnByStatus('Engage')
  );
  const prospectColumn = useAppSelector(
    crmSalesSelectors.selectCrmSalesColumnByStatus('Prospect')
  );
  const closeDealColumn = useAppSelector(
    crmSalesSelectors.selectCrmSalesColumnByStatus('Close Deal')
  );
  const lostColumn = useAppSelector(
    crmSalesSelectors.selectCrmSalesColumnByStatus('Lost')
  );

  const fetchTicketsByStatus = useCallback(
    async (status: CrmSalesStatus, filters?: CrmSalesFilterPayload) => {
      try {
        const fetchFilters = getFetchFilters(filters);
        const response = unwrapResult(
          await dispatch(
            crmSalesActions.fetchCrmSalesTicket({
              ...fetchFilters,
              status
            })
          )
        );

        return response;
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  const fetchAllTickets = useCallback(
    async (filters?: CrmSalesFilterPayload) => {
      try {
        const fetchFilters = getFetchFilters(filters);
        await Promise.all(
          crmSalesStatuses.map((status) =>
            dispatch(
              crmSalesActions.fetchCrmSalesTicket({
                ...fetchFilters,
                status
              })
            )
          )
        );
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  const addTicket = useCallback(
    async (
      payload: CreateCrmSalesTicketPayload,
      filters?: CrmSalesFilterPayload
    ) => {
      try {
        const response = unwrapResult(
          await dispatch(crmSalesActions.addCrmSalesTicket(payload))
        );

        await fetchTicketsByStatus(payload.status, filters);

        return response;
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, fetchTicketsByStatus]
  );

  const updateTicket = useCallback(
    async (
      payload: UpdateCrmSalesTicketPayload,
      filters?: CrmSalesFilterPayload
    ) => {
      try {
        const response = unwrapResult(
          await dispatch(crmSalesActions.updateCrmSalesTicket(payload))
        );

        if (payload.data.status) {
          await fetchTicketsByStatus(payload.data.status, filters);
        }

        if (
          payload.previous_status &&
          payload.previous_status !== payload.data.status
        ) {
          await fetchTicketsByStatus(payload.previous_status, filters);
        }

        return response;
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, fetchTicketsByStatus]
  );

  return {
    statuses: crmSalesStatuses,
    columns,

    leadColumn,
    warmColumn,
    engageColumn,
    prospectColumn,
    closeDealColumn,
    lostColumn,

    fetchTicketsByStatus,
    fetchAllTickets,
    addTicket,
    updateTicket
  };
};
